import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useSite } from "../lib/site-context";
import TurnstileWidget from "../components/TurnstileWidget";

const SCANNER_OPTIONS = [
  { id: "vorhanden", title_de: "Intraoralscanner vorhanden", sub_de: "im Einsatz, regelmäßig genutzt", title_en: "Scanner in use", sub_en: "in regular use" },
  { id: "wenig_genutzt", title_de: "Scanner vorhanden", sub_de: "bisher wenig genutzt", title_en: "Scanner available", sub_en: "rarely used" },
  { id: "geplant", title_de: "Noch kein Scanner", sub_de: "Anschaffung geplant", title_en: "No scanner yet", sub_en: "purchase planned" },
];

const SERVICE_OPTIONS = [
  { id: "kronen_bruecken", de: "Kronen & Brücken", en: "Crowns & Bridges" },
  { id: "zirkon", de: "Zirkonkronen", en: "Zirconia crowns" },
  { id: "vmk_nem", de: "VMK / NEM", en: "VMK / NEM" },
  { id: "implantatprothetik", de: "Implantatprothetik", en: "Implant prosthetics" },
  { id: "schnarchschienen", de: "Schnarchschienen", en: "Anti-snoring splints" },
  { id: "kombiniert", de: "Kombinierter Zahnersatz", en: "Combined prosthetics" },
  { id: "teleskop", de: "Teleskopversorgungen", en: "Telescope prosthetics" },
  { id: "sonstiges", de: "Sonstige digitale Versorgungen", en: "Other digital prosthetics" },
];

const PRODUCT_OPTIONS = [
  { id: "kronen_bruecken", de: "Kronen & Brücken", en: "Crowns & Bridges" },
  { id: "vmk_nem", de: "VMK & NEM", en: "VMK & NEM" },
  { id: "implantatprothetik", de: "Implantatprothetik", en: "Implant prosthetics" },
  { id: "teleskop", de: "Teleskopversorgungen", en: "Telescope prosthetics" },
  { id: "schnarchschienen", de: "Schnarchschienen", en: "Anti-snoring splints" },
  { id: "gemischt", de: "Gemischt / verschiedene", en: "Mixed / various" },
];

const CONCERN_OPTIONS = [
  { id: "kva", de: "Kostenvoranschlag / Preisbeispiel", en: "Cost estimate / price example" },
  { id: "testfall", de: "Kostenloser Testfall", en: "Free test case" },
  { id: "scanner", de: "Scanner-Kompatibilität prüfen", en: "Check scanner compatibility" },
  { id: "schulung", de: "Einstieg & Schulung", en: "Onboarding & training" },
  { id: "rueckruf", de: "Rückruf vereinbaren", en: "Arrange a callback" },
];

export default function PraxisCheckPage() {
  const { lang, t } = useSite();
  const [params] = useSearchParams();
  const [step, setStep] = React.useState(1);
  const [done, setDone] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [security, setSecurity] = React.useState({ token: "", website: "" });
  const [turnstileToken, setTurnstileToken] = React.useState("");
  const [turnstileReset, setTurnstileReset] = React.useState(0);
  const [data, setData] = React.useState({
    scanner_status: "",
    services: [],
    product: "",
    product_interest: "",
    praxisname: "",
    name: "",
    email: "",
    phone: "",
    scanner_model: "",
    case_volume: "",
    concern: "",
    message: "",
    consent: false,
    source_page: "/praxis-check",
  });

  // When arriving from a Leistungen service card (?produkt=…), surface the chosen
  // product and pre-select the matching option in step 2.
  React.useEffect(() => {
    const p = params.get("produkt");
    if (!p) return;
    const norm = p.trim().toLowerCase();
    const match = PRODUCT_OPTIONS.find(o => o.de.toLowerCase() === norm || o.en.toLowerCase() === norm)
      || SERVICE_OPTIONS.find(o => o.de.toLowerCase() === norm || o.en.toLowerCase() === norm);
    setData(d => ({
      ...d,
      product_interest: p,
      product: (match && PRODUCT_OPTIONS.some(o => o.id === match.id)) ? match.id : d.product,
      source_page: `/praxis-check?produkt=${p}`,
    }));
  }, [params]);

  const loadToken = React.useCallback(async () => {
    try {
      const result = await api.formToken("praxis_check");
      setSecurity((old) => ({ ...old, token: result.token }));
    } catch {
      toast.error(t("Das Formular konnte nicht vorbereitet werden. Bitte laden Sie die Seite neu.", "The form could not be prepared. Please reload the page."));
    }
  }, [t]);

  React.useEffect(() => { loadToken(); }, [loadToken]);

  const update = (k, v) => setData(d => ({ ...d, [k]: v }));
  const toggleService = (s) => setData(d => ({ ...d, services: d.services.includes(s) ? d.services.filter(x => x !== s) : [...d.services, s] }));

  const canNext = step === 1 ? !!data.scanner_status : step === 2 ? (!!data.product || data.services.length > 0) : true;

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.submitPraxisCheck({ ...data, type: "praxis_check", _form_token: security.token, _website: security.website, _turnstile_token: turnstileToken });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Fehler beim Senden. Bitte erneut versuchen.", "Sending failed. Please try again."));
      await loadToken();
      setTurnstileReset((value) => value + 1);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main className="min-h-screen pt-32 pb-20 bg-[var(--bg)]">
        <div className="max-w-3xl mx-auto px-5 md:px-10 text-center">
          <div className="brut-tag mb-8 mx-auto inline-flex">{t("Anfrage gesendet", "Request sent")}</div>
          <h1 className="brut-h1 mb-6">{t("Danke!", "Thank you!")}</h1>
          <p className="brut-body max-w-xl mx-auto">{t("Wir melden uns persönlich. Bitte keine Patienten- oder Scandaten über dieses Formular senden.", "We will contact you personally. Please do not send patient or scan data via this form.")}</p>
          <Link to="/" className="brut-btn mt-10">{t("Zurück zur Startseite", "Back to home")} <ArrowUpRight size={16} /></Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-32 pb-20 bg-[var(--bg)]" data-testid="praxis-check-page">
      <div className="max-w-[1100px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-8 md:mb-12">
          <div className="col-span-12 md:col-span-5">
            <div className="brut-tag mb-6">PRAXIS-CHECK / 03 SCHRITTE</div>
            <h1 className="brut-h1">{t("Passt der digitale Weg zu Ihrer Praxis?", "Is the digital path right for your practice?")}</h1>
          </div>
          <aside className="col-span-12 md:col-span-7 md:pl-12 md:border-l md:border-[var(--line)] flex flex-col justify-end">
            <p className="brut-body">{t("In drei kurzen Schritten klären wir Scannerstatus, geeignete Versorgungen und die nächsten Schritte.", "In three short steps we clarify scanner status, suitable prosthetics and next steps.")}</p>
            <p className="brut-body opacity-70 mt-4 text-sm">{t("Hinweis: Bitte keine Patienten-, Gesundheits- oder Scandaten über dieses Formular senden. Für Falldaten wird ein gesicherter Übertragungsweg bereitgestellt.", "Note: Please do not send patient, health or scan data through this form. A secured transfer channel will be provided for case data.")}</p>
          </aside>
        </div>

        {/* Selected product banner (when arriving from a Leistungen card) */}
        {data.product_interest && (
          <div className="mb-8 flex flex-wrap items-center gap-x-4 gap-y-1 border-2 border-[var(--ink)] bg-[var(--accent)] text-[var(--ink)] px-5 py-4" data-testid="product-interest-banner">
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase opacity-80">{t("Ihre Auswahl", "Your selection")}</span>
            <span className="font-cond font-black uppercase tracking-tight text-lg leading-tight">{data.product_interest}</span>
          </div>
        )}

        {/* Progress indicator — informational only, not clickable */}
        <div className="mb-8 md:mb-12" data-testid="praxis-stepper" aria-hidden="true">
          <div className="flex items-center gap-2 max-w-sm">
            {[1, 2, 3].map((n, i) => (
              <React.Fragment key={n}>
                <span
                  data-testid={`stepper-${n}`}
                  className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs border-2 transition-colors ${step > n ? "bg-[var(--accent)] border-[var(--ink)] text-[var(--ink)]" : step === n ? "bg-[var(--ink)] border-[var(--ink)] text-[var(--bg)]" : "border-[var(--line)] text-[var(--muted)] bg-[var(--bg)]"}`}
                >
                  {step > n ? <Check size={14} /> : n}
                </span>
                {i < 2 && <span className={`flex-1 h-[2px] ${step > n ? "bg-[var(--accent)]" : "bg-[var(--line)]"}`} />}
              </React.Fragment>
            ))}
          </div>
          <div className="mt-3 font-mono text-[11px] tracking-[0.2em] uppercase text-[var(--muted)]">
            {t("Schritt", "Step")} {step} {t("von", "of")} 3 — {step === 1 ? t("Scanner", "Scanner") : step === 2 ? t("Leistung", "Service") : t("Kontakt", "Contact")}
          </div>
        </div>

        {/* Steps */}
        <div className="border-2 border-[var(--ink)] p-6 md:p-10 brut-shadow">
          {step === 1 && (
            <div className="space-y-4" data-testid="step-1">
              <h2 className="brut-h2 mb-6">{t("Wie ist der Scannerstatus Ihrer Praxis?", "What is your practice's scanner status?")}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-[var(--ink)]">
                {SCANNER_OPTIONS.map((opt, i) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => update("scanner_status", opt.id)}
                    data-testid={`scanner-opt-${opt.id}`}
                    className={`text-left p-6 md:p-8 ${i !== 2 ? "md:border-r-2 border-b-2 md:border-b-0 border-[var(--ink)]" : ""} ${data.scanner_status === opt.id ? "bg-[var(--ink)] text-[var(--bg)]" : ""}`}
                  >
                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase opacity-70 mb-3">0{i+1}</div>
                    <div className="brut-h3 mb-2">{lang === "en" ? opt.title_en : opt.title_de}</div>
                    <div className="brut-body opacity-90">{lang === "en" ? opt.sub_en : opt.sub_de}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="step-2">
              <h2 className="brut-h2 mb-6">{t("Welche Versorgungen sind relevant?", "Which prosthetics are relevant?")}</h2>
              <div className="mb-8 max-w-xl">
                <label className="brut-label">{t("Hauptsächlich gewünschtes Produkt", "Main product of interest")}</label>
                <select
                  data-testid="product-select"
                  className="brut-select"
                  value={data.product}
                  onChange={e => update("product", e.target.value)}
                >
                  <option value="">{t("— Bitte wählen —", "— Please select —")}</option>
                  {PRODUCT_OPTIONS.map(p => (
                    <option key={p.id} value={p.id}>{lang === "en" ? p.en : p.de}</option>
                  ))}
                </select>
              </div>
              <p className="brut-body opacity-70 mb-6">{t("Weitere relevante Versorgungen (Mehrfachauswahl möglich)", "Further relevant prosthetics (multi-select possible)")}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-2 border-[var(--ink)]">
                {SERVICE_OPTIONS.map((s, i) => {
                  const active = data.services.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggleService(s.id)}
                      data-testid={`service-opt-${s.id}`}
                      className={`flex items-center justify-between p-5 md:p-6 ${i % 2 === 0 ? "md:border-r-2" : ""} border-b-2 last:border-b-0 border-[var(--ink)] ${active ? "bg-[var(--ink)] text-[var(--bg)]" : ""}`}
                    >
                      <span className="font-cond font-black uppercase tracking-tight text-xl text-left">{lang === "en" ? s.en : s.de}</span>
                      <span className={`w-6 h-6 border-2 border-current flex items-center justify-center ${active ? "bg-[var(--accent)]" : ""}`}>
                        {active && <Check size={14} />}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="step-3">
              <div className="absolute -left-[10000px] w-px h-px overflow-hidden" aria-hidden="true">
                <label>Website<input tabIndex={-1} autoComplete="off" value={security.website} onChange={(e) => setSecurity((old) => ({ ...old, website: e.target.value }))} /></label>
              </div>
              <h2 className="brut-h2 mb-6">{t("Ihre Kontaktdaten", "Your contact details")}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="brut-label">{t("Praxisname *", "Practice name *")}</label>
                  <input data-testid="input-praxisname" className="brut-input" value={data.praxisname} onChange={e => update("praxisname", e.target.value)} required />
                </div>
                <div>
                  <label className="brut-label">{t("Vor- und Nachname *", "Full name *")}</label>
                  <input data-testid="input-name" className="brut-input" value={data.name} onChange={e => update("name", e.target.value)} required />
                </div>
                <div>
                  <label className="brut-label">{t("E-Mail *", "Email *")}</label>
                  <input data-testid="input-email" type="email" className="brut-input" value={data.email} onChange={e => update("email", e.target.value)} required />
                </div>
                <div>
                  <label className="brut-label">{t("Telefon", "Phone")}</label>
                  <input data-testid="input-phone" className="brut-input" value={data.phone} onChange={e => update("phone", e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <label className="brut-label">{t("Scanner-Hersteller und Modell (falls vorhanden)", "Scanner manufacturer and model (if any)")}</label>
                  <input data-testid="input-scanner" className="brut-input" value={data.scanner_model} onChange={e => update("scanner_model", e.target.value)} />
                </div>
                <div>
                  <label className="brut-label">{t("Monatliche Fallzahl (ca.)", "Monthly case volume (approx.)")}</label>
                  <input data-testid="input-case-volume" className="brut-input" value={data.case_volume} onChange={e => update("case_volume", e.target.value)} placeholder={t("z.B. 10–20", "e.g. 10–20")} />
                </div>
                <div>
                  <label className="brut-label">{t("Ihr Anliegen", "Your concern")}</label>
                  <select data-testid="input-concern" className="brut-select" value={data.concern} onChange={e => update("concern", e.target.value)}>
                    <option value="">{t("— Bitte wählen —", "— Please select —")}</option>
                    {CONCERN_OPTIONS.map(c => <option key={c.id} value={c.id}>{lang === "en" ? c.en : c.de}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="brut-label">{t("Nachricht", "Message")}</label>
                  <textarea data-testid="input-message" className="brut-textarea" rows={4} value={data.message} onChange={e => update("message", e.target.value)} />
                </div>
                <label className="md:col-span-2 flex gap-3 items-start text-sm leading-relaxed">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={data.consent}
                    onChange={e => update("consent", e.target.checked)}
                    data-testid="input-consent"
                  />
                  <span>
                    {t("Ich habe die ", "I have read the ")}
                    <Link to="/datenschutz" className="underline font-bold">{t("Datenschutzerklärung", "privacy policy")}</Link>
                    {t(" gelesen und stimme der Verarbeitung meiner Angaben zur Bearbeitung der Anfrage zu.", " and consent to the processing of my information for handling this request.")}
                  </span>
                </label>
                <div className="md:col-span-2">
                  <TurnstileWidget onToken={setTurnstileToken} resetKey={turnstileReset} language={lang} />
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-3 sm:items-center sm:justify-between mt-10 pt-8 border-t-2 border-[var(--ink)]">
            <button
              type="button"
              disabled={step === 1}
              onClick={() => setStep(s => Math.max(1, s - 1))}
              className="brut-btn brut-btn-ghost disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="step-back"
            >
              <ChevronLeft size={16} /> {t("Zurück", "Back")}
            </button>
            {step < 3 ? (
              <button
                type="button"
                disabled={!canNext}
                onClick={() => setStep(s => s + 1)}
                className="brut-btn disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="step-next"
              >
                {t("Weiter", "Next")} <ChevronRight size={16} />
              </button>
            ) : (
              <button
                type="button"
                disabled={submitting || !security.token || !turnstileToken || !data.praxisname || !data.name || !data.email || !data.consent}
                onClick={submit}
                className="brut-btn disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="step-submit"
              >
                {submitting ? t("Wird gesendet …", "Sending …") : t("Anfrage senden", "Send request")} <ArrowUpRight size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
